import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { useTranslation } from 'react-i18next';
import { vociDellaBarra, gruppoDelPercorso } from '../../utils/vociBarra';
import { getCurrentUserId, getCurrentUsername, isPlayer } from '../../utils/currentUser';
import { lingua, lingueDisponibili, impostaLingua } from '../../preferenze';
import { frase } from '../../frase';
import { avanzamentoBonus, conBonus, mancaDaGiocare } from '../../utils/conto';
import BarraBonus from '../BarraBonus';
import FinestraAccesso from './components/auth/FinestraAccesso';
import FinestraRegistrazione from './components/auth/FinestraRegistrazione';
import AvvisoSessione from './components/AvvisoSessione';
import SkinLogo from '../Shell/SkinLogo';
/* IL MODULO TESTATA (orologio, cerca partita, colori) e la rotellina che il
   modulo Schedina puo' mettere qui: pezzi a se', montati da questa barra. */
import Orologio from './testata/Orologio';
import CercaPartita from './testata/CercaPartita';
import RotellinaTestata from './testata/RotellinaTestata';
import {paeseDellaLingua} from './testata/bandierine';
import './testata/Testata.css';
import Bandiera from '../common/Bandiera';
import {testata as moduloTestata, schedina as moduloSchedina} from '../../moduli/leggi';
import {eTelefono, osservaSchermo} from '../SportMenu/sport-central/schermo';
import {eColore} from '../../skin';
import './styles/BarraAlta.css';
import SpiaRiproposte from './SpiaRiproposte';
import Busta from './Busta';
import {LivelloInTestata} from '../Pagine/Fedelta';
import './SpiaRiproposte.css';

/**
 * LA STRISCIA IN ALTO.
 *
 * Prende il posto del menu a sinistra - quindici voci in sei sezioni - e del
 * "pannello di controllo", che era lo stesso menu ridisegnato a bottoni. Le
 * voci restano quelle che i permessi concedono: le calcola vociBarra.js
 * partendo da getMyAccountSections, cioe' dallo stesso posto di prima.
 *
 * Una sola implementazione per scrivania e telefono. Prima l'intestazione era
 * scritta due volte nello stesso file, un ramo per misura, e il ramo mobile
 * non montava nemmeno il blocco del conto: da telefono non c'era modo di
 * uscire. Qui la striscia va a capo, e a capo ci va da sola.
 */

/* LE CIFRE DEL CONTO, coi nomi che hanno davvero.
 *
 * Arrivano da GET /private/credit/{userId}/figures (credit-service), che e'
 * la stessa chiamata che il backoffice usa per la rete commerciale:
 * NetworkFigures. Prima qui c'erano nomi italiani inventati - saldoUtente,
 * disponibilita, fidoDisp, saldoTotRete - che nessun servizio scrive: la
 * pastiglia del saldo non sarebbe mai comparsa. Li avevo copiati
 * dall'intestazione vecchia, che infatti il saldo non lo mostrava.
 *
 * Il significato di ogni cifra e' quello calcolato da NetworkFiguresService,
 * non una nostra interpretazione.
 */
const CIFRE = [
  // Quello che ho in mano io. Le etichette si traducono dove si scrivono (t).
  { chiave: 'available',      etichetta: frase('Disponibilità'),  blocco: 'mio' },
  { chiave: 'userBalance',    etichetta: frase('Saldo utente'),   blocco: 'mio' },
  // Il saldo bonus del casino' (casino-service, non il conto): non si preleva.
  { chiave: 'bonusBalance',   etichetta: frase('Saldo bonus'),    blocco: 'mio', bonus: true, larga: true },
  // Quello che ho distribuito, e come sta la rete sotto di me.
  { chiave: 'networkCredit',  etichetta: frase('Fido rete'),      blocco: 'rete' },
  { chiave: 'creditAvailable', etichetta: frase('Fido disp.'),    blocco: 'rete' },
  { chiave: 'networkBalance', etichetta: frase('Saldo tot. rete'), blocco: 'rete', larga: true },
];

function leggiCifra(dati, chiave) {
  if (!dati) return null;
  const v = dati[chiave];
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

/* La valuta la dice il conto, non la decide la pagina: una rete puo' non
   essere in euro. Se non arriva non si mette niente al posto suo. */
function simbolo(valuta) {
  if (!valuta) return '';
  const noti = { EUR: '\u20ac', USD: '$', GBP: '\u00a3' };
  return noti[String(valuta).toUpperCase()] || String(valuta).toUpperCase();
}

/* I separatori seguono la lingua di chi guarda: 1.234,56 in italiano,
   1,234.56 in inglese. */
function inCifre(n) {
  return Number(n || 0).toLocaleString(lingua(), {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

/**
 * LA TENDINA DELLA LINGUA. Le lingue sono quelle della skin (preferenze:
 * lingueDisponibili), col nome scritto nella lingua stessa; la scelta resta
 * nel browser e vale finche' non la si cambia. Con una lingua sola non
 * compare: non c'e' niente da scegliere.
 */
function TendinaLingua({ aperta, apri, entra, esce, t }) {
  const lingue = lingueDisponibili();
  const corrente = lingua();
  if (lingue.length < 2) return null;
  const scegli = (codice) => { impostaLingua(codice); apri(null); };
  return (
      <div
          className={'voce-barra a-destra lingua' + (aperta === 'lingua' ? ' aperta' : '')}
          onMouseEnter={() => entra('lingua')}
          onMouseLeave={esce}
      >
        <button type="button" className="pastiglia-lingua" aria-label={t('Lingua')}
                aria-haspopup="listbox" aria-expanded={aperta === 'lingua'}
                onClick={() => apri((a) => (a === 'lingua' ? null : 'lingua'))}>
          <span className="k">{t('Lingua')}</span>
          <Bandiera categoria={{paese: paeseDellaLingua(corrente), name: corrente}} className="bandierina" />
          <span className="v">{corrente.toUpperCase()}</span>
        </button>
        <div className="tendina-barra stretta" role="listbox" aria-label={t('Lingua')}>
          {lingue.map((l) => (
              <button
                  type="button"
                  role="option"
                  aria-selected={l.codice === corrente}
                  className={l.codice === corrente ? 'voce-tendina qui' : 'voce-tendina'}
                  key={l.codice}
                  lang={l.codice}
                  onClick={() => scegli(l.codice)}
              >
                <span><Bandiera categoria={{paese: paeseDellaLingua(l.codice), name: l.nome}} className="bandierina" /> {l.nome}</span>
                <span className="nota-tendina">{l.codice.toUpperCase()}</span>
              </button>
          ))}
        </div>
      </div>
  );
}

const CHIAVE_COPERTO = 'SALDO_COPERTO';

function BarraAlta({ userService }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const barra = useRef(null);
  const chiusura = useRef(null);

  const [dentro, setDentro] = useState(false);
  /* Sotto i 1024 px (schermo.js, la stessa soglia di tutto il sito) niente
     orologio e la ricerca e' una lente. */
  const [telefono, setTelefono] = useState(eTelefono());
  useEffect(() => osservaSchermo((stretto) => setTelefono(stretto)), []);
  const modulo = moduloTestata();
  const rotellina = moduloSchedina().rotellina;
  /* I colori propri del modulo: variabili sulla barra, e basta. */
  const stileTestata = modulo.colori ? {} : undefined;
  if (modulo.colori && eColore(modulo.colori.fondo)) stileTestata['--lvc-nav-1'] = modulo.colori.fondo.trim();
  if (modulo.colori && eColore(modulo.colori.testo)) {
    stileTestata['--lvc-text-1'] = modulo.colori.testo.trim();
    stileTestata['--lvc-text-2'] = 'color-mix(in srgb, ' + modulo.colori.testo.trim() + ' 72%, transparent)';
  }
  const pezziDestra = (
      <>
        {telefono && modulo.cerca ? <CercaPartita telefono /> : null}
        {rotellina !== 'schedina' ? <RotellinaTestata /> : null}
      </>
  );
  const [datiConto, setDatiConto] = useState(null);
  const [aperta, setAperta] = useState(null);          // quale tendina e' aperta
  const [accesso, setAccesso] = useState(false);       // la finestra di Accedi
  const [motivoAccesso, setMotivoAccesso] = useState('barra');
  const [dettaglioAccesso, setDettaglioAccesso] = useState(null); // perche' la sessione e' finita
  // la finestra di REGISTRATI: aperta/chiusa e la query da cui si viene (?code=… da Telegram, ?invito=…)
  const [registrazione, setRegistrazione] = useState(null);

  /* La barra in basso chiede di entrare.
     Sul telefono il pulsante "Entra" sta li', dove arriva il pollice, ma la
     finestra di accesso e' una sola e vive qui: la barra lo chiede al
     servizio, questa la apre. Senza, ci sarebbero due finestre di accesso da
     tenere allineate -- e la seconda sarebbe quella che si dimentica. */
  useEffect(() => {
    if (!userService || !userService.accessoRichiesto) return undefined;
    const sub = userService.accessoRichiesto().subscribe(() => {
      setMotivoAccesso('barra');
      setDettaglioAccesso(null);
      setAccesso(true);
    });
    return () => sub.unsubscribe();
  }, [userService]);

  /* REGISTRATI: come Accedi, una finestra sopra la pagina e non una pagina.
     La chiedono il pulsante qui, «Registrati» nella finestra di accesso e la
     route /register (RegistrazioneDaLink), anche al ritorno da Telegram. */
  useEffect(() => {
    if (!userService || typeof userService.registrazioneRichiesta !== 'function') return undefined;
    const sub = userService.registrazioneRichiesta().subscribe((r) => {
      setAccesso(false);
      setRegistrazione({ricerca: (r && r.ricerca) || '', telegram: (r && r.telegram) || null, avviso: !!(r && r.avviso)});
    });
    return () => sub.unsubscribe();
  }, [userService]);
  /* Cambiata pagina (conto in attesa, home dopo l'accesso automatico), la finestra si chiude. */
  useEffect(() => { setRegistrazione(null); }, [location.pathname]);
  const apriRegistrazione = () => { setAccesso(false); setRegistrazione({ricerca: ''}); };

  /* LA SESSIONE E' FINITA: scaduta per inattivita', durata massima, chiusa
     da un nuovo accesso, chiusa da un operatore. Il custode lo dice con il
     motivo scritto e qui si apre la finestra di accesso con quel motivo come
     titolo: la persona sa perche' e' fuori e rientra da dov'era, con la
     schedina conservata. */
  useEffect(() => {
    if (!userService || typeof userService.fineSessione !== 'function') return undefined;
    const sub = userService.fineSessione().subscribe((perche) => {
      if (!perche) return;
      setMotivoAccesso('scaduta');
      setDettaglioAccesso(perche);
      setAccesso(true);
    });
    return () => sub.unsubscribe();
  }, [userService]);
  const [coperto, setCoperto] = useState(() => {
    try { return window.localStorage.getItem(CHIAVE_COPERTO) === '1'; } catch (e) { return false; }
  });

  const percorso = location.pathname;
  const nome = (userService && typeof userService.getUsername === 'function'
      && userService.getUsername()) || getCurrentUsername() || '';

  /* I permessi stanno nel token e si leggono subito. La pagina del conto
     chiedeva /api/rbac/my-permissions e teneva il menu vuoto finche' non
     rispondeva, ma getMyAccountSections quella risposta non la guarda
     nemmeno: legge i permessi dal token. Qui niente attesa e niente
     scheletro. */
  const gruppi = dentro ? vociDellaBarra() : [];
  const qui = gruppoDelPercorso(gruppi, percorso);
  const soloGiocatore = isPlayer();

  // ---------------------------------------------------------------- il conto
  /* Il gateway, cioe' REACT_APP_API_URL. Prima era REACT_APP_BACKOFFICE_API_URL,
     che in public/env-config.js NON C'E': la chiamata usciva con "undefined"
     davanti e la funzione si fermava prima ancora di partire. E' lo stesso
     motivo per cui il saldo non si e' mai visto nell'intestazione vecchia. */
  const leggiConto = useCallback(async () => {
    const env = window.env || {};
    const base = env.REACT_APP_API_URL;
    const token = window.localStorage.getItem('access_token');
    const id = getCurrentUserId();
    if (!base || !token || !id) return;
    try {
      const { data } = await axios.get(`${base}/private/credit/${id}/figures`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (data) setDatiConto(await conBonus(data));
    } catch (e) {
      // Il conto che non arriva non deve portarsi dietro la barra: resta
      // senza cifre e tutto il resto funziona.
      console.warn('Saldo non disponibile:', e && e.message);
    }
  }, []);

  useEffect(() => {
    const stop = new Subject();
    userService.isLoggedIn().pipe(takeUntil(stop)).subscribe((acceso) => {
      setDentro(acceso);
      if (acceso) leggiConto(); else setDatiConto(null);
    });

    // Chi compie un'operazione avvisa: la barra si riallinea senza che
    // nessuna pagina debba saperlo.
    const alSaldo = () => leggiConto();
    window.addEventListener('balanceUpdated', alSaldo);
    /* E OGNI VOLTA CHE UNA GIOCATA CAMBIA: una riproposta rifiutata o
       scaduta riporta la posta sul conto, una accettata a importo piu' basso
       ne riporta una parte, una giocata mandata la prende. L'evento lo
       mandano la schedina, il ticket aperto e il flusso del giocatore; la
       barra rilegge e il saldo in testa dice il numero vero senza
       ricaricare la pagina. */
    window.addEventListener('giocataCambiata', alSaldo);
    if (userService.walletSubject && typeof userService.walletSubject.subscribe === 'function') {
      userService.walletSubject.pipe(takeUntil(stop)).subscribe(() => leggiConto());
    }

    return () => {
      window.removeEventListener('balanceUpdated', alSaldo);
      window.removeEventListener('giocataCambiata', alSaldo);
      stop.next();
      stop.complete();
    };
  }, [userService, leggiConto]);

  // ------------------------------------------------------------- le tendine
  const alTatto = () => window.matchMedia('(hover: none)').matches
      || window.matchMedia('(max-width: 760px)').matches;

  const entra = (chiave) => {
    if (alTatto()) return;
    clearTimeout(chiusura.current);
    setAperta(chiave);
  };
  const esce = () => {
    if (alTatto()) return;
    // Un ritardo in chiusura: senza, basta sfiorare il bordo fra la voce e la
    // tendina e il menu sparisce mentre ci si sta andando.
    chiusura.current = setTimeout(() => setAperta(null), 180);
  };

  const clicSullaVoce = (gruppo) => {
    if (alTatto()) {
      setAperta((a) => (a === gruppo.chiave ? null : gruppo.chiave));
      return;
    }
    // Da scrivania, cliccare il nome del gruppo porta alla sua predefinita:
    // chi sa gia' dove andare fa un gesto solo.
    const prima = gruppo.voci[0];
    if (prima) vai(prima.percorso);
  };

  const vai = (dove) => { setAperta(null); navigate(dove); };

  // Cliccare fuori chiude quello che c'e' aperto.
  useEffect(() => {
    const fuori = (e) => {
      if (barra.current && !barra.current.contains(e.target)) setAperta(null);
    };
    const allaTastiera = (e) => { if (e.key === 'Escape') setAperta(null); };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', allaTastiera);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', allaTastiera);
    };
  }, []);

  // Cambiando pagina non deve restare aperto niente.
  useEffect(() => { setAperta(null); }, [percorso]);

  // ---------------------------------------------------------------- l'uscita
  const esci = () => {
    setAperta(null);
    try {
      const sessione = window.localStorage.getItem('CLIENT_SESSION');
      userService.doLogout(sessione);
    } finally {
      setDatiConto(null);
      // Prima l'uscita lasciava la persona esattamente dov'era, e spesso
      // quella era una pagina che senza conto non ha senso.
      navigate('/');
    }
  };

  const cambiaCoperto = () => {
    setCoperto((c) => {
      const nuovo = !c;
      try { window.localStorage.setItem(CHIAVE_COPERTO, nuovo ? '1' : '0'); } catch (e) { /* va bene lo stesso */ }
      return nuovo;
    });
  };

  // --------------------------------------------------------------- il saldo
  /* La regola e' quella del backoffice, non una nostra: chi non puo' avere
     figli vede solo il proprio saldo. Le cifre della rete - fido, fido
     distribuito, rollup dal basso - per un giocatore non vogliono dire
     niente, e in fila si scambierebbero per soldi che ha in mano. */
  const val = simbolo(datiConto && datiConto.currency);
  const inSoldi = (n) => `${inCifre(n)}${val ? ` ${val}` : ''}`;

  const cifre = CIFRE
      .filter((c) => (soloGiocatore ? c.chiave === 'userBalance' || c.bonus : !c.bonus))
      .map((c) => ({ ...c, valore: leggiCifra(datiConto, c.chiave) }))
      // Una cifra che il servizio non manda non diventa zero: sparisce.
      .filter((c) => c.valore !== null);
  const cifreMie = cifre.filter((c) => c.blocco === 'mio');
  const cifreRete = cifre.filter((c) => c.blocco === 'rete');

  // Nella pastiglia ci sta una cifra sola, e dev'essere quella che si guarda
  // per sapere quanto si puo' giocare o distribuire.
  const inPastiglia = soloGiocatore
      ? { etichetta: t('Saldo'), valore: leggiCifra(datiConto, 'userBalance') }
      : { etichetta: t('Disp.'), valore: leggiCifra(datiConto, 'available') };

  const blocco = (titolo, elenco) => (
      <>
        {!soloGiocatore ? <div className="titolo-blocco">{titolo}</div> : null}
        <div className="cifre-conto">
          {elenco.map((c) => (
              <div className={c.larga ? 'cifra-sola larga' : 'cifra-sola'} key={c.chiave}>
                <span className="k">{t(c.etichetta)}</span>
                <span className="v">{inSoldi(c.valore)}</span>
                {c.bonus && mancaDaGiocare(datiConto) > 0 ? (
                    <BarraBonus
                        avanzamento={avanzamentoBonus(datiConto)}
                        testo={t('Giocati {{giocato}} su {{requisito}}', {
                          giocato: inSoldi(avanzamentoBonus(datiConto).giocato),
                          requisito: inSoldi(avanzamentoBonus(datiConto).requisito),
                        })} />
                ) : null}
                {c.bonus && mancaDaGiocare(datiConto) > 0 ? (
                    <span className="nota-bonus">
                      {t('Non prelevabile: gioca ancora {{importo}} per sbloccarlo', { importo: inSoldi(mancaDaGiocare(datiConto)) })}
                    </span>
                ) : null}
              </div>
          ))}
        </div>
      </>
  );

  return (
      <>
        <div className="barra-alta" ref={barra} style={stileTestata}>
          <div className="marchio" onClick={() => navigate('/')} role="button" tabIndex={0}
               onKeyDown={(e) => { if (e.key === 'Enter') navigate('/'); }}>
            <SkinLogo />
          </div>
          {!telefono && modulo.orologio ? <Orologio /> : null}
          {!telefono && modulo.cerca ? <CercaPartita /> : null}

          {dentro ? (
              <div className="gruppo-destro">
                {gruppi.map((g) => (
                    <div
                        key={g.chiave}
                        className={'voce-barra'
                            + (aperta === g.chiave ? ' aperta' : '')
                            + (qui === g.chiave ? ' qui' : '')}
                        onMouseEnter={() => entra(g.chiave)}
                        onMouseLeave={esce}
                    >
                      <button type="button" onClick={() => clicSullaVoce(g)}>
                        {t(g.etichetta)}
                      </button>
                      <div className="tendina-barra">
                        {g.voci.map((v) => (
                            <React.Fragment key={v.percorso}>
                              {v.staccato ? <div className="riga-tendina" /> : null}
                              <button
                                  type="button"
                                  className={percorso === v.percorso ? 'voce-tendina qui' : 'voce-tendina'}
                                  onClick={() => vai(v.percorso)}
                              >
                                <span>{t(v.etichetta)}</span>
                                {v.nota ? <span className="nota-tendina">{t(v.nota)}</span> : null}
                                {v.predefinita ? <span className="predef">{t('predefinita')}</span> : null}
                              </button>
                            </React.Fragment>
                        ))}
                      </div>
                    </div>
                ))}

                <div className="destra-barra">
                  {/* I SOLDI HANNO UN CASSETTO LORO, non quello della
                      navigazione: dentro non c'e' nessun link. Le pagine
                      stanno in "Il mio conto", una voce in un posto solo. */}
                  {/* Il livello del programma fedelta' (Pagine/Fedelta.js), prima del saldo: porta a /fedelta. */}
                  <LivelloInTestata classe={'livello-barra'} />
                  {inPastiglia.valore !== null ? (
                      <div
                          className={'voce-barra a-destra saldo' + (aperta === 'saldo' ? ' aperta' : '')}
                          onMouseEnter={() => entra('saldo')}
                          onMouseLeave={esce}
                      >
                        <button
                            type="button"
                            className={coperto ? 'pastiglia-saldo coperta' : 'pastiglia-saldo'}
                            onClick={() => setAperta((a) => (a === 'saldo' ? null : 'saldo'))}
                        >
                          <span className="k">{inPastiglia.etichetta}</span>
                          <span className="v">{coperto ? '•••••' : inSoldi(inPastiglia.valore)}</span>
                        </button>
                        <div className="tendina-barra stretta">
                          {cifreMie.length ? blocco(t('Il mio conto'), cifreMie) : null}
                          {cifreRete.length ? <div className="riga-tendina" /> : null}
                          {cifreRete.length ? blocco(t('La rete'), cifreRete) : null}
                          <div className="riga-tendina" />
                          <button type="button" className="occhio" onClick={cambiaCoperto}>
                            {coperto ? t("Mostra l'importo nella barra") : t("Nascondi l'importo nella barra")}
                          </button>
                        </div>
                      </div>
                  ) : null}

                  {/* La busta dei messaggi, accanto al saldo: il numero sono i non letti. */}
                  <Busta dentro={dentro} />

                  {/* «TI HANNO RIPROPOSTO», su ogni pagina: compare solo
                      quando c'e' una riproposta che aspetta, e porta al
                      ticket. Chi sta in Home o nel casino' la vede qui. */}
                  <SpiaRiproposte dentro={dentro} navigate={navigate} />
                  {pezziDestra}
                  <TendinaLingua aperta={aperta} apri={setAperta} entra={entra} esce={esce} t={t} />
                  <span className="chi">{nome}</span>
                  <button type="button" className="esci" onClick={esci}>{t('ESCI')}</button>
                </div>
              </div>
          ) : (
              /* Senza conto non c'e' niente da raggruppare: al posto delle
                 tendine restano le due azioni che servono. */
              <div className="destra-barra">
                {pezziDestra}
                <TendinaLingua aperta={aperta} apri={setAperta} entra={entra} esce={esce} t={t} />
                <button type="button" className="entra-barra" onClick={() => setAccesso(true)}>
                  {t('Accedi')}
                </button>
                <button type="button" className="registrati-barra" onClick={apriRegistrazione}>
                  {t('Registrati')}
                </button>
              </div>
          )}
        </div>

        <FinestraAccesso
            aperta={accesso}
            motivo={motivoAccesso}
            dettaglio={dettaglioAccesso}
            userService={userService}
            onChiudi={() => {
              setAccesso(false);
              setDettaglioAccesso(null);
              setMotivoAccesso('barra');
              if (typeof userService?.consumaFineSessione === 'function') userService.consumaFineSessione();
            }}
            onRegistrati={apriRegistrazione}
        />

        <FinestraRegistrazione
            aperta={!!registrazione}
            ricerca={registrazione ? registrazione.ricerca : ''}
            telegram={registrazione ? registrazione.telegram : null}
            avviso={!!(registrazione && registrazione.avviso)}
            userService={userService}
            onChiudi={() => setRegistrazione(null)}
            onAccedi={() => { setRegistrazione(null); setMotivoAccesso('barra'); setDettaglioAccesso(null); setAccesso(true); }}
        />

        <AvvisoSessione userService={userService} />
      </>
  );
}

export default BarraAlta;
